import React, { useState } from 'react';
import { X, Search, Star, ArrowUpRight, Sparkles } from 'lucide-react';
import { CategoryItem, ToolItem, ThemeMode } from '../types';
import { getToolVisualTheme } from '../data/toolVisuals';

interface CategoryModalProps {
  category: CategoryItem | null;
  onClose: () => void;
  theme: ThemeMode;
  favorites: string[];
  onToggleFavorite: (toolId: string) => void;
  onLaunchTool: (tool: ToolItem) => void;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({
  category,
  onClose,
  theme,
  favorites,
  onToggleFavorite,
  onLaunchTool
}) => {
  if (!category) return null;

  const [modalSearch, setModalSearch] = useState('');
  const isDark = theme === 'dark';

  const filteredTools = category.tools.filter(tool => 
    tool.name.toLowerCase().includes(modalSearch.toLowerCase()) ||
    tool.description.toLowerCase().includes(modalSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className={`relative w-full max-w-3xl max-h-[85vh] rounded-2xl border flex flex-col shadow-2xl overflow-hidden ${
          isDark
            ? 'bg-[#0f121e] border-slate-800 text-white shadow-purple-950/30'
            : 'bg-white border-slate-200 text-slate-900 shadow-slate-300'
        }`}
      >
        {/* Header */}
        <div className={`p-5 sm:p-6 border-b flex items-center justify-between ${
          isDark ? 'border-slate-800 bg-[#121626]' : 'border-slate-100 bg-slate-50/80'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${category.gradientFrom} ${category.gradientTo} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
              {category.title.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight">{category.title}</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {category.countText} • {category.description}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-colors ${
              isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Search Bar */}
        <div className={`p-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
          <div className={`flex items-center px-3.5 py-2.5 rounded-xl border ${
            isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-slate-100/80 border-slate-200'
          }`}>
            <Search className={`w-4 h-4 mr-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              placeholder={`Search in ${category.title}...`}
              className="w-full bg-transparent outline-none text-sm"
            />
          </div>
        </div>

        {/* Tools List */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-3 custom-scrollbar flex-1">
          {filteredTools.length === 0 ? (
            <div className={`text-center py-12 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              No tools matching "{modalSearch}"
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {filteredTools.map((tool) => {
                const isFav = favorites.includes(tool.id);
                const visual = getToolVisualTheme({ id: tool.id, name: tool.name, categoryId: category.id });
                const IconComp = visual.icon;
                return (
                  <div
                    key={tool.id}
                    onClick={() => onLaunchTool(tool)}
                    className={`group p-3 sm:p-4 rounded-xl border transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                      isDark
                        ? 'bg-slate-900/60 border-slate-800/90 hover:border-purple-500/50 hover:bg-slate-800/70'
                        : 'bg-slate-50/70 border-slate-200 hover:border-purple-300 hover:bg-purple-50/40'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5 sm:mb-2">
                        <div className="flex items-center gap-2 font-bold text-[13px] sm:text-sm min-w-0">
                          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${visual.bgClass} flex items-center justify-center shrink-0`}>
                            <IconComp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                          </div>
                          <span className="truncate">{tool.name}</span>
                          {tool.isPopular && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-extrabold bg-purple-500/20 text-purple-400 border border-purple-500/30 shrink-0">
                              Popular
                            </span>
                          )}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite(tool.id);
                          }}
                          className="p-1 text-slate-400 hover:text-amber-400 transition-colors shrink-0"
                          title="Favorite tool"
                        >
                          <Star className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isFav ? 'fill-amber-400 text-amber-400' : ''}`} />
                        </button>
                      </div>

                      <p className={`text-[10px] sm:text-xs line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {tool.description}
                      </p>
                    </div>

                    <div className="mt-2 sm:mt-3 flex items-center justify-between text-[10px] sm:text-xs font-semibold text-purple-500 group-hover:text-purple-400">
                      <span>Open Tool</span>
                      <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t text-center text-xs ${
          isDark ? 'border-slate-800 text-slate-500 bg-[#0d101a]' : 'border-slate-100 text-slate-400 bg-slate-50'
        }`}>
          Dualis Tools • All tools run locally in your web browser for 100% privacy
        </div>
      </div>
    </div>
  );
};
